import { useContext } from "react"
import { useQuery, type UseQueryOptions } from "@tanstack/react-query"

import { isGID, type GID } from "@unlikelystudio/commerce-connector"

import type { InferReturn } from "~/@types/generics"
import type { I18nLocale } from "~/lib/i18n/types"
import { getSfProductsByIds } from "~/lib/shopify/queries/get-sf-products-by-id"
import useLocale from "~/hooks/useLocale"
import { RecentlyViewedContext } from "~/providers/RecentlyViewedProvider"
import { getRecentlyViewedProductsFromLocale } from "~/providers/RecentlyViewedProvider/utils/get-products"

type QueryData = InferReturn<typeof getSfProductsByIds>["products"]
type QueryKey = ["get-recently-viewed", { locale: I18nLocale; GIDs: GID[] }]

export function useRecentlyViewed(queryOptions: UseQueryOptions<QueryData, unknown, QueryData, QueryKey>) {
  const locale = useLocale() as I18nLocale
  const context = useContext(RecentlyViewedContext)

  const products = getRecentlyViewedProductsFromLocale(locale, context)
  const GIDs = products?.map((entry) => entry?.id)?.filter(isGID) ?? []

  return useQuery<QueryData, unknown, QueryData, QueryKey>({
    queryKey: ["get-recently-viewed", { locale, GIDs }],
    queryFn: async () => {
      try {
        const response = await getSfProductsByIds({ ids: GIDs, locale, nextTags: GIDs })
        return response?.products?.filter(Boolean) ?? []
      } catch {
        return []
      }
    },
    ...queryOptions,
    enabled: Boolean(GIDs?.length > 0 && queryOptions?.enabled),
  })
}
